import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Upload, Music, X, Check, Loader2,
  AlertCircle, Play, Pause, Trash2, RefreshCw, Search
} from 'lucide-react';
import { api } from '../../services/api';

const API_URL = api.getApiUrl();

const SFX_CATEGORIES = [
  { id: 'apple', name: 'Apple', icon: '🍎' },
  { id: 'bells', name: 'Bells', icon: '🔔' },
  { id: 'cameras', name: 'Cameras', icon: '📷' },
  { id: 'cinematics', name: 'Cinematics', icon: '🎬' },
  { id: 'computer', name: 'Computer', icon: '💻' },
  { id: 'diversos', name: 'Diversos', icon: '🎵' },
  { id: 'gears', name: 'Gears', icon: '⚙️' },
  { id: 'hits-impacts', name: 'Hits - Impacts', icon: '💥' },
  { id: 'ihphone', name: 'iPhone', icon: '📱' },
  { id: 'pop-click', name: 'Pop - Click (Bounce)', icon: '🎯' },
  { id: 'risers', name: 'Risers', icon: '📈' },
  { id: 'ui', name: 'UI', icon: '🖱️' },
  { id: 'wooshes', name: 'Wooshes', icon: '💨' }
];

const AdminAddSFX = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  // ─── TABS ───────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState('upload'); // 'upload' | 'manage'

  // ─── UPLOAD STATE ───────────────────────────────────────
  const [category, setCategory] = useState('cinematics');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [playingId, setPlayingId] = useState(null);

  // ─── MANAGE STATE ───────────────────────────────────────
  const [sfxList, setSfxList] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [filterCategory, setFilterCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  // ─── LOAD SFX LIST ─────────────────────────────────────
  const loadSFXList = async () => {
    setLoadingList(true);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_URL}/api/sfx?limit=500`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Erro ao carregar SFX');
      const data = await res.json();
      setSfxList(data.sfx || data || []);
    } catch (err) {
      console.error('Erro ao carregar lista SFX:', err);
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'manage') loadSFXList();
  }, [activeTab]);

  // ─── DELETE SFX ────────────────────────────────────────
  const handleDeleteSFX = async (sfxId) => {
    setDeletingId(sfxId);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_URL}/api/sfx/${sfxId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Erro ao deletar SFX');
      setSfxList(prev => prev.filter(s => s.id !== sfxId));
      setConfirmDelete(null);
    } catch (err) {
      console.error('Erro ao deletar:', err);
      alert('Erro ao deletar SFX. Tente novamente.');
    } finally {
      setDeletingId(null);
    }
  };

  // ─── UPLOAD HANDLERS ────────────────────────────────────
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const audioFiles = files.filter(file =>
      file.type.startsWith('audio/') ||
      /\.(mp3|wav|ogg|m4a|aac|flac)$/i.test(file.name)
    );
    if (audioFiles.length === 0) {
      setError('Nenhum arquivo de áudio válido selecionado');
      return;
    }
    const newFiles = audioFiles.map(file => ({
      id: Math.random().toString(36).substr(2, 9),
      file,
      title: file.name.replace(/\.(mp3|wav|ogg|m4a|aac|flac)$/i, ''),
      tags: [],
      status: 'pending',
      audioUrl: URL.createObjectURL(file)
    }));
    setSelectedFiles(prev => [...prev, ...newFiles]);
    setError('');
  };

  const removeFile = (id) => {
    setSelectedFiles(prev => {
      const file = prev.find(f => f.id === id);
      if (file?.audioUrl) URL.revokeObjectURL(file.audioUrl);
      return prev.filter(f => f.id !== id);
    });
  };

  const updateFileTitle = (id, title) =>
    setSelectedFiles(prev => prev.map(f => f.id === id ? { ...f, title } : f));

  const updateFileTags = (id, tags) =>
    setSelectedFiles(prev => prev.map(f => f.id === id ? { ...f, tags } : f));

  const togglePlay = (id) => {
    const audio = document.getElementById(`audio-${id}`);
    if (!audio) return;
    if (playingId === id) {
      audio.pause();
      setPlayingId(null);
    } else {
      if (playingId) {
        const prev = document.getElementById(`audio-${playingId}`);
        if (prev) prev.pause();
      }
      audio.play();
      setPlayingId(id);
    }
  };

  const handleUploadAll = async () => {
    if (selectedFiles.length === 0) {
      setError('Selecione pelo menos um arquivo de áudio');
      return;
    }
    setSaving(true);
    setError('');
    for (const fileItem of selectedFiles) {
      if (fileItem.status === 'success') continue;
      setSelectedFiles(prev => prev.map(f => f.id === fileItem.id ? { ...f, status: 'uploading' } : f));
      try {
        const formData = new FormData();
        formData.append('file', fileItem.file);
        const token = localStorage.getItem('admin_token');
        if (!token) throw new Error('Token de autenticação não encontrado. Faça login novamente.');

        const uploadResponse = await fetch(`${API_URL}/api/upload-sfx`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });
        if (uploadResponse.status === 401) throw new Error('Sessão expirada. Por favor, faça login novamente.');
        if (!uploadResponse.ok) {
          const errorData = await uploadResponse.json().catch(() => ({}));
          throw new Error(errorData.detail || `Erro no upload: ${uploadResponse.status}`);
        }
        const uploadData = await uploadResponse.json();

        const sfxPayload = {
          title: fileItem.title,
          category,
          tags: fileItem.tags,
          audio_url: uploadData.audio_url,
          audio_public_id: uploadData.audio_public_id,
          duration: uploadData.duration || 0,
          format: uploadData.format || 'mp3',
          is_premium: false
        };
        await api.createSFX(sfxPayload);
        setSelectedFiles(prev => prev.map(f => f.id === fileItem.id ? { ...f, status: 'success' } : f));
      } catch (err) {
        if (err.message.includes('Sessão expirada') || err.message.includes('Token de autenticação')) {
          setError(err.message);
          setSaving(false);
          setSelectedFiles(prev => prev.map(f => f.status === 'uploading' ? { ...f, status: 'error', errorMsg: err.message } : f));
          setTimeout(() => { localStorage.removeItem('admin_token'); navigate('/admin/login'); }, 3000);
          return;
        }
        setSelectedFiles(prev => prev.map(f => f.id === fileItem.id ? { ...f, status: 'error', errorMsg: err.message } : f));
      }
    }
    setSaving(false);
    const allSuccess = selectedFiles.every(f => f.status === 'success');
    if (allSuccess) setTimeout(() => setSelectedFiles([]), 2000);
  };

  // ─── FILTERED LIST ─────────────────────────────────────
  const filteredSFX = sfxList.filter(s => {
    const matchCat = filterCategory === 'all' || s.category === filterCategory;
    const matchSearch = !searchQuery || s.title?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchCat && matchSearch;
  });

  return (
    <div className="min-h-screen bg-[#05050A] text-white">
      <div className="max-w-5xl mx-auto p-8">

        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Link to="/admin" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
            <ArrowLeft className="w-5 h-5" />
            Voltar ao Admin
          </Link>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('upload')}
            className={`px-6 py-3 rounded-xl font-bold text-sm transition-all flex items-center gap-2 ${
              activeTab === 'upload'
                ? 'bg-purple-500 text-white'
                : 'bg-white/5 text-gray-400 hover:text-white hover:bg-white/10'
            }`}
          >
            <Upload className="w-4 h-4" />
            Upload SFX
          </button>
          <button
            onClick={() => setActiveTab('manage')}
            className={`px-6 py-3 rounded-xl font-bold text-sm transition-all flex items-center gap-2 ${
              activeTab === 'manage'
                ? 'bg-red-500 text-white'
                : 'bg-white/5 text-gray-400 hover:text-white hover:bg-white/10'
            }`}
          >
            <Trash2 className="w-4 h-4" />
            Gerenciar SFX
            {sfxList.length > 0 && (
              <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs">{sfxList.length}</span>
            )}
          </button>
        </div>

        {/* ─── TAB: UPLOAD ───────────────────────────────────── */}
        {activeTab === 'upload' && (
          <div className="bg-[#0D0D0D] rounded-2xl border border-white/10 p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                <Music className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">Adicionar SFX</h1>
                <p className="text-slate-400 text-sm">Efeitos sonoros para a Conta E</p>
              </div>
            </div>

            {error && (
              <div className="mb-6 bg-red-500/10 border border-red-500/50 rounded-xl p-4 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-red-500 font-bold text-sm">{error}</p>
                  {error.includes('Sessão expirada') && (
                    <p className="text-red-400 text-xs mt-1">Redirecionando para login...</p>
                  )}
                </div>
              </div>
            )}

            {/* Categoria */}
            <div className="mb-6">
              <label className="block text-gray-400 text-xs font-bold uppercase mb-3">Categoria do SFX</label>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {SFX_CATEGORIES.map((cat) => (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => setCategory(cat.id)}
                    className={`p-3 rounded-xl border font-bold text-xs transition-all ${
                      category === cat.id
                        ? 'bg-purple-500 border-purple-500 text-white'
                        : 'bg-white/5 border-white/10 text-gray-400 hover:text-white hover:border-white/20'
                    }`}
                  >
                    <span className="text-lg block mb-1">{cat.icon}</span>
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Drop zone */}
            <div className="mb-6">
              <label className="block text-gray-400 text-xs font-bold uppercase mb-3">Arquivos de Áudio</label>
              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-white/20 rounded-xl p-8 text-center cursor-pointer hover:border-purple-500/50 transition-all bg-white/5 hover:bg-white/10"
              >
                <Upload className="w-12 h-12 text-slate-500 mx-auto mb-3" />
                <p className="text-white font-semibold mb-1">Clique para selecionar arquivos</p>
                <p className="text-slate-400 text-sm">MP3, WAV, OGG, M4A, AAC, FLAC</p>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="audio/*,.mp3,.wav,.ogg,.m4a,.aac,.flac"
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>

            {/* Lista de arquivos selecionados */}
            {selectedFiles.length > 0 && (
              <div className="space-y-3 mb-6">
                {selectedFiles.map((fileItem) => (
                  <div
                    key={fileItem.id}
                    className={`bg-white/5 rounded-xl p-4 border ${
                      fileItem.status === 'success' ? 'border-green-500/50'
                      : fileItem.status === 'error' ? 'border-red-500/50'
                      : fileItem.status === 'uploading' ? 'border-purple-500/50'
                      : 'border-white/10'
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <button
                        onClick={() => togglePlay(fileItem.id)}
                        className="w-10 h-10 bg-purple-500/20 hover:bg-purple-500/30 rounded-lg flex items-center justify-center transition-colors flex-shrink-0"
                        disabled={fileItem.status === 'uploading'}
                      >
                        {playingId === fileItem.id
                          ? <Pause className="w-5 h-5 text-purple-400" />
                          : <Play className="w-5 h-5 text-purple-400 ml-0.5" />
                        }
                      </button>
                      <audio id={`audio-${fileItem.id}`} src={fileItem.audioUrl} onEnded={() => setPlayingId(null)} />
                      <div className="flex-1 min-w-0">
                        <input
                          type="text"
                          value={fileItem.title}
                          onChange={(e) => updateFileTitle(fileItem.id, e.target.value)}
                          placeholder="Título do SFX"
                          className="w-full bg-white/10 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-purple-500/50 mb-2"
                          disabled={fileItem.status === 'uploading' || fileItem.status === 'success'}
                        />
                        <input
                          type="text"
                          value={fileItem.tags.join(', ')}
                          onChange={(e) => updateFileTags(fileItem.id, e.target.value.split(',').map(t => t.trim()).filter(Boolean))}
                          placeholder="Tags (separadas por vírgula)"
                          className="w-full bg-white/10 border border-white/10 rounded-lg px-3 py-2 text-xs text-slate-300 placeholder-slate-500 focus:outline-none focus:border-purple-500/50"
                          disabled={fileItem.status === 'uploading' || fileItem.status === 'success'}
                        />
                        {fileItem.errorMsg && <p className="text-red-400 text-xs mt-2">{fileItem.errorMsg}</p>}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {fileItem.status === 'uploading' && <Loader2 className="w-5 h-5 text-purple-500 animate-spin" />}
                        {fileItem.status === 'success' && <Check className="w-5 h-5 text-green-500" />}
                        {fileItem.status === 'error' && <AlertCircle className="w-5 h-5 text-red-500" />}
                        {fileItem.status === 'pending' && (
                          <button onClick={() => removeFile(fileItem.id)} className="p-1 hover:bg-red-500/20 rounded transition-colors">
                            <X className="w-5 h-5 text-red-400" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <button
              onClick={handleUploadAll}
              disabled={saving || selectedFiles.length === 0}
              className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 disabled:from-gray-600 disabled:to-gray-600 text-white font-bold py-4 rounded-xl transition-all disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {saving ? (
                <><Loader2 className="w-5 h-5 animate-spin" />Enviando SFX...</>
              ) : (
                <><Upload className="w-5 h-5" />Enviar {selectedFiles.length} SFX para Conta E</>
              )}
            </button>
          </div>
        )}

        {/* ─── TAB: GERENCIAR SFX ─────────────────────────────── */}
        {activeTab === 'manage' && (
          <div className="bg-[#0D0D0D] rounded-2xl border border-white/10 p-8">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-orange-500 rounded-xl flex items-center justify-center">
                  <Trash2 className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold">Gerenciar SFX</h1>
                  <p className="text-slate-400 text-sm">{sfxList.length} SFX no banco de dados</p>
                </div>
              </div>
              <button
                onClick={loadSFXList}
                disabled={loadingList}
                className="flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-sm text-gray-400 hover:text-white transition-all"
              >
                <RefreshCw className={`w-4 h-4 ${loadingList ? 'animate-spin' : ''}`} />
                Atualizar
              </button>
            </div>

            {/* Filtros */}
            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input
                  type="text"
                  placeholder="Buscar por título..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 py-3 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-purple-500/50"
                />
              </div>
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-purple-500/50"
              >
                <option value="all">Todas categorias</option>
                {SFX_CATEGORIES.map(c => (
                  <option key={c.id} value={c.id}>{c.icon} {c.name}</option>
                ))}
              </select>
            </div>

            {/* Lista */}
            {loadingList ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-8 h-8 text-purple-500 animate-spin" />
                <span className="ml-3 text-slate-400">Carregando SFX...</span>
              </div>
            ) : filteredSFX.length === 0 ? (
              <div className="text-center py-16 text-slate-500">
                <Music className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Nenhum SFX encontrado</p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredSFX.map((sfx) => (
                  <div
                    key={sfx.id}
                    className="flex items-center justify-between bg-white/5 border border-white/10 rounded-xl px-4 py-3 hover:border-white/20 transition-all"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="text-xl flex-shrink-0">
                        {SFX_CATEGORIES.find(c => c.id === sfx.category)?.icon || '🎵'}
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-white truncate">{sfx.title}</p>
                        <p className="text-xs text-slate-500">
                          {SFX_CATEGORIES.find(c => c.id === sfx.category)?.name || sfx.category}
                          {sfx.duration ? ` · ${sfx.duration.toFixed(1)}s` : ''}
                        </p>
                      </div>
                    </div>

                    {/* Confirm delete */}
                    {confirmDelete === sfx.id ? (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-red-400">Confirmar?</span>
                        <button
                          onClick={() => handleDeleteSFX(sfx.id)}
                          disabled={deletingId === sfx.id}
                          className="px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white text-xs font-bold rounded-lg transition-all flex items-center gap-1"
                        >
                          {deletingId === sfx.id
                            ? <Loader2 className="w-3 h-3 animate-spin" />
                            : <Trash2 className="w-3 h-3" />
                          }
                          Deletar
                        </button>
                        <button
                          onClick={() => setConfirmDelete(null)}
                          className="px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white text-xs font-bold rounded-lg transition-all"
                        >
                          Cancelar
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setConfirmDelete(sfx.id)}
                        className="p-2 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all flex-shrink-0"
                        title="Deletar SFX"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAddSFX;
